import { useId, useState } from "react";
import { ArrowRight, Plus } from "lucide-react";

type FAQSectionProps = {
  onRequestAccess: () => void;
};

const faqItems = [
  {
    question: "What exactly is M4MMON?",
    answer: "M4MMON is a research workspace for turning trading ideas into versioned Python projects, controlled builds, and inspectable historical backtests. It is built for traders who want to examine evidence—not consume signals.",
  },
  {
    question: "Can I write real Python strategies?",
    answer: "Yes. The beta supports normal multi-file Python 3.12 projects. You can work directly in the source or ask the built-in coding agent to help inspect, edit, check, and build it under the same project constraints.",
  },
  {
    question: "What market data is included?",
    answer: "The beta uses a fixed one-minute order-flow dataset covering BTCUSDT, ETHUSDT, BNBUSDT, SOLUSDT, XRPUSDT, ADAUSDT, DOGEUSDT, AVAXUSDT, LINKUSDT, and DOTUSDT. It does not imply arbitrary market coverage or published raw tick access.",
  },
  {
    question: "What does order flow mean here?",
    answer: "Each one-minute candle carries participation context derived from aggregated trades, including buy and sell volume, delta, trade counts, volume imbalance, and cumulative volume delta alongside price and volume fields.",
  },
  {
    question: "Can I reproduce a backtest result?",
    answer: "That is the point of the workflow. A run remains connected to its immutable source revision, locked build, dataset identity, configuration, status, and resulting artifacts so the path to the result stays inspectable.",
  },
  {
    question: "What can the coding agent actually do?",
    answer: "It can list and read project files, apply compare-and-swap edits, run project checks, create a locked build, start one backtest, and inspect its state. It has no arbitrary runtime network access, no runtime credentials, and no permission to change source silently.",
  },
  {
    question: "Does M4MMON trade live or connect to brokers?",
    answer: "No. The beta is historical research software. It does not connect to a brokerage, route orders, or place live trades.",
  },
  {
    question: "How do access, pricing, and launch timing work?",
    answer: "Access is currently invite-only, with a public waitlist, a beta-request route, and account creation for people who already have an invite. Pricing and broader launch timing have not been announced; confirmed signups receive access updates.",
  },
] as const;

export function FAQSection({ onRequestAccess }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const baseId = useId();

  return (
    <section aria-labelledby="faq-title" className="chapter faq" id="faq">
      <div className="chapter-frame faq__layout">
        <header className="faq__intro">
          <h2 id="faq-title">Questions worth asking.</h2>
          <p>What the beta does, what it does not, and what sits behind every result.</p>
          <button className="faq__cta primary-glass-button" onClick={onRequestAccess} type="button">
            Request beta access <ArrowRight aria-hidden="true" size={17} />
          </button>
          <div aria-label="Beta boundaries" className="faq__boundaries">
            <span><i aria-hidden="true" /> Invite-only beta</span>
            <span><i aria-hidden="true" /> Historical research only</span>
          </div>
        </header>

        <div className="faq__register">
          {faqItems.map((item, index) => {
            const isOpen = openIndex === index;
            const questionId = `${baseId}-question-${index}`;
            const answerId = `${baseId}-answer-${index}`;

            return (
              <article className={`faq__item${isOpen ? " is-open" : ""}`} key={item.question}>
                <h3>
                  <button
                    aria-controls={answerId}
                    aria-expanded={isOpen}
                    id={questionId}
                    onClick={() => setOpenIndex((current) => current === index ? null : index)}
                    type="button"
                  >
                    <span className="faq__index">{String(index + 1).padStart(2, "0")}</span>
                    <span className="faq__question">{item.question}</span>
                    <span aria-hidden="true" className="faq__toggle"><Plus size={17} /></span>
                  </button>
                </h3>
                <div
                  aria-hidden={!isOpen}
                  aria-labelledby={questionId}
                  className="faq__answer"
                  id={answerId}
                  role="region"
                >
                  <div><p>{item.answer}</p></div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
